import { Badge, Button, useToast, type Tone } from '@cartly/ui';
import { Check, Copy, Smartphone } from 'lucide-react';
import { useState } from 'react';

/**
 * Marks a number that is also in use on the WhatsApp Business app (Meta "coexistence"): it is
 * already registered, so no registration step applies.
 */
export function BizAppBadge() {
  return (
    <span
      className="badge row"
      title="Also used on the WhatsApp Business app (coexistence). No registration needed."
    >
      <Smartphone size={12} aria-hidden="true" />
      WhatsApp Business app
    </span>
  );
}

/** Read-only value with a copy button (URLs, ids, one-time secrets). */
export function CopyField({
  label,
  value,
  secret = false,
}: {
  label: string;
  value: string;
  secret?: boolean;
}) {
  const toast = useToast();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Copy failed — select the text and copy it manually');
    }
  };
  return (
    <div className="field">
      <span className="field__label">{label}</span>
      <div className="copy-field">
        <code
          className={secret ? 'copy-field__value copy-field__value--secret' : 'copy-field__value'}
        >
          {value}
        </code>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => void copy()}
          icon={copied ? <Check size={14} /> : <Copy size={14} />}
          aria-label={`Copy ${label}`}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </div>
  );
}

/** Pretty-printed JSON. React escapes the text, so payload content cannot inject markup. */
export function JsonView({ value }: { value: unknown }) {
  return <pre className="json-view">{JSON.stringify(value, null, 2)}</pre>;
}

const STATUS_TONES: Record<string, Tone> = {
  active: 'success',
  processed: 'success',
  succeeded: 'success',
  accepted: 'success',
  paid: 'success',
  fulfilled: 'success',
  delivered: 'success',
  completed: 'success',
  recovered: 'primary',
  pending: 'warning',
  open: 'info',
  authorized: 'info',
  partial: 'info',
  partially_refunded: 'warning',
  abandoned: 'warning',
  unfulfilled: 'neutral',
  duplicate: 'neutral',
  draft: 'neutral',
  failed: 'critical',
  dead: 'critical',
  rejected: 'critical',
  error: 'critical',
  refunded: 'critical',
  voided: 'critical',
  disconnected: 'neutral',
  archived: 'neutral',
};

const STATUS_LABELS: Record<string, string> = {
  // The store did not report a payment status.
  unknown: 'not reported',
};

export function StatusBadge({ status, label }: { status: string; label?: string }) {
  return (
    <Badge tone={STATUS_TONES[status] ?? 'neutral'}>
      {label ?? STATUS_LABELS[status] ?? status.replace(/_/g, ' ')}
    </Badge>
  );
}

/** "Load more" footer for keyset-paginated tables. */
export function LoadMore({ onClick, loading }: { onClick: () => void; loading: boolean }) {
  return (
    <div className="card__footer">
      <Button size="sm" loading={loading} onClick={onClick}>
        Load more
      </Button>
    </div>
  );
}

/** Previous / Next controls for client-side paged lists. `page` is zero-based. */
export function Pager({
  page,
  pageSize,
  total,
  onChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  onChange: (page: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = page * pageSize + 1;
  const to = Math.min(total, (page + 1) * pageSize);
  return (
    <div className="row" style={{ justifyContent: 'space-between', padding: '12px 16px' }}>
      <span className="text-muted">
        {from}–{to} of {total}
      </span>
      <div className="row">
        <Button
          size="sm"
          variant="secondary"
          disabled={page <= 0}
          onClick={() => onChange(page - 1)}
        >
          Previous
        </Button>
        <span className="text-muted">
          Page {page + 1} of {pages}
        </span>
        <Button
          size="sm"
          variant="secondary"
          disabled={page >= pages - 1}
          onClick={() => onChange(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
